import { c as _c } from "react/compiler-runtime";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import type { Entry } from "contentful";
import Image from "next/image";
import { EmailForm } from "@/components/custom/EmailForm/EmailForm";
import { getEntryOfContentType } from "@/lib/contentful/guards";
import { cn } from "@/lib/utils";
import type { TrustpilotRating } from "@/services/TrustpilotClient";
import type { TypeEmailFormSkeleton, TypeFooterSkeleton } from "@/types/contentful/generated";
import { FooterLegalBar } from "./FooterLegalBar";
import { FooterNavGrid } from "./FooterNavGrid";
import { FooterReviews } from "./FooterReviews";
import { FooterSocialLinks } from "./FooterSocialLinks";
type EmailFormEntry = Entry<TypeEmailFormSkeleton, undefined, string>;
type FooterFields = Entry<TypeFooterSkeleton, undefined, string>["fields"];
interface Props {
  fields: FooterFields;
  reviews: TrustpilotRating | null;
}
export function FooterView(t0) {
  const $ = _c(44);
  const {
    fields,
    reviews
  } = t0;
  let t1;
  if ($[0] !== fields.newsletterForm) {
    t1 = getEntryOfContentType(fields.newsletterForm, "emailForm");
    $[0] = fields.newsletterForm;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const emailForm = t1;
  const newsletterText = fields.newsletterText;
  const t2 = reviews ? "md:grid-cols-3" : "md:grid-cols-2";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid grid-cols-1 gap-6 md:items-start", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== reviews) {
    t4 = reviews && <FooterReviews data={reviews} />;
    $[4] = reviews;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== reviews) {
    t5 = reviews && <hr className="md:hidden border-t border-border" />;
    $[6] = reviews;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== newsletterText) {
    t6 = newsletterText && <div className="text-left text-base font-normal md:p-6 [&_h3]:heading-md">{documentToReactComponents(newsletterText)}</div>;
    $[8] = newsletterText;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== emailForm || $[11] !== newsletterText) {
    t7 = emailForm && <div className={cn(!newsletterText && "md:col-span-2")}><EmailForm {...emailForm.fields} errorVariant="badge" onDarkSurface={true} /></div>;
    $[10] = emailForm;
    $[11] = newsletterText;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== emailForm) {
    t8 = emailForm && <hr className="md:hidden border-t border-border" />;
    $[13] = emailForm;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className={t3}>{t4}{t5}{t6}{t7}{t8}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== fields.navSections) {
    t10 = fields.navSections ?? [];
    $[22] = fields.navSections;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <FooterNavGrid navSections={t10} />;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t9) {
    t12 = <div className="mx-auto flex max-w-site-content flex-col gap-12 p-6 md:p-12">{t9}{t11}</div>;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="flex justify-center px-6 pt-12.5 pb-5 md:px-12"><Image src="/images/every_sticker_footer_img.png" alt="Every sticker" width={350} height={142} className="h-auto max-w-full" /></div>;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== fields.socialMediaIcons) {
    t14 = fields.socialMediaIcons ?? [];
    $[30] = fields.socialMediaIcons;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <FooterSocialLinks socialMediaIcons={t14} />;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <hr className="md:hidden mx-6 border-t border-border" />;
    t17 = <div className="hidden md:block mx-6 border-t border-border" />;
    $[34] = t16;
    $[35] = t17;
  } else {
    t16 = $[34];
    t17 = $[35];
  }
  let t18;
  if ($[36] !== fields.legalLinks) {
    t18 = fields.legalLinks ?? [];
    $[36] = fields.legalLinks;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <FooterLegalBar legalLinks={t18} />;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t12 || $[41] !== t15 || $[42] !== t19) {
    t20 = <footer className="w-full bg-card-foreground text-primary-foreground">{t12}{t13}{t15}{t16}{t17}{t19}</footer>;
    $[40] = t12;
    $[41] = t15;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}